import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Video, Users, Calendar, Music, Edit, Plus, 
  Play, Eye, Clock, X
} from 'lucide-react';
import { toast } from 'sonner';

interface MeetupRoom {
  meetup_id: string;
  event_id: string;
  title: string;
  description?: string;
  room_code: string;
  participant_count: number;
  max_participants: number;
  status: string;
  created_at: string;
}

interface ArtistEvent {
  event_id: string;
  title: string;
  date: string;
  venue: string;
  tickets_sold: number;
}

export default function ArtistDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [artistProfile, setArtistProfile] = useState<any>(null);
  const [meetupRooms, setMeetupRooms] = useState<MeetupRoom[]>([]);
  const [artistEvents, setArtistEvents] = useState<ArtistEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [roomForm, setRoomForm] = useState({
    title: '',
    description: '',
    max_participants: 10
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Load artist profile
      const profile = await api.getMyArtistProfile();
      setArtistProfile(profile);
      
      // Load artist's events
      const events = await api.getMyEvents();
      setArtistEvents(events);
      
    } catch (error: any) {
      if (error.message?.includes('not an artist')) {
        navigate('/become-artist');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createMeetupRoom = async () => {
    if (!selectedEventId) {
      toast.error('Please select an event');
      return;
    }
    if (!roomForm.title) {
      toast.error('Please enter a room title');
      return;
    }

    try {
      const room = await api.createMeetupRoom({
        event_id: selectedEventId,
        title: roomForm.title,
        description: roomForm.description,
        max_participants: roomForm.max_participants
      });
      
      toast.success('Meetup room created!');
      setShowCreateRoom(false);
      setRoomForm({ title: '', description: '', max_participants: 10 });
      
      // Navigate to the room
      navigate(`/virtual-room/${selectedEventId}?meetup=${room.meetup_id}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to create room');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!artistProfile) {
    return (
      <div className="text-center py-20">
        <Music className="h-16 w-16 text-gray-600 mx-auto mb-4" />
        <h2 className="text-2xl font-heading font-bold mb-4">Become an Artist</h2>
        <p className="text-gray-400 mb-6">Apply to become a verified artist and connect with your fans</p>
        <Button onClick={() => navigate('/become-artist')} className="shadow-glow">
          Apply Now
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Artist Dashboard</h1>
          <p className="text-gray-400">Welcome back, {artistProfile.stage_name}</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={() => navigate('/profile')} className="glass-hover">
            <Edit className="h-4 w-4 mr-2" />
            Edit Profile
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full bg-primary/10">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{artistProfile.follower_count || 0}</p>
                <p className="text-sm text-gray-400">Followers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full bg-secondary/10">
                <Calendar className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{artistEvents.length}</p>
                <p className="text-sm text-gray-400">Events</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full bg-green-500/10">
                <Video className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{meetupRooms.filter(r => r.status === 'active').length}</p>
                <p className="text-sm text-gray-400">Active Rooms</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full bg-yellow-500/10">
                <Eye className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {artistEvents.reduce((sum, e) => sum + (e.tickets_sold || 0), 0)}
                </p>
                <p className="text-sm text-gray-400">Total Tickets Sold</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Meetup Room Section */}
      <Card className="glass border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center">
            <Video className="h-5 w-5 mr-2 text-primary" />
            Virtual Meetup Rooms
          </CardTitle>
          <Button onClick={() => setShowCreateRoom(true)} className="shadow-glow">
            <Plus className="h-4 w-4 mr-2" />
            Create Room
          </Button>
        </CardHeader>
        <CardContent>
          {showCreateRoom && (
            <Card className="glass border-primary/30 mb-6">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold">Create New Meetup Room</h3>
                  <Button variant="ghost" size="icon" onClick={() => setShowCreateRoom(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <Label>Select Event</Label>
                  <select
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    className="w-full h-10 px-3 rounded-md glass border border-white/10 bg-transparent text-foreground"
                  >
                    <option value="" className="bg-background-paper">Select an event...</option>
                    {artistEvents.map((event) => (
                      <option key={event.event_id} value={event.event_id} className="bg-background-paper">
                        {event.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Room Title</Label>
                  <Input
                    value={roomForm.title}
                    onChange={(e) => setRoomForm({ ...roomForm, title: e.target.value })}
                    placeholder="e.g., Meet & Greet with fans"
                    className="glass border-white/10"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Textarea
                    value={roomForm.description}
                    onChange={(e) => setRoomForm({ ...roomForm, description: e.target.value })}
                    placeholder="What will you discuss in this session?"
                    className="glass border-white/10"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Max Participants</Label>
                  <Input
                    type="number"
                    value={roomForm.max_participants}
                    onChange={(e) => setRoomForm({ ...roomForm, max_participants: parseInt(e.target.value) })}
                    min={2}
                    max={50}
                    className="glass border-white/10 w-32"
                  />
                </div>

                <Button onClick={createMeetupRoom} className="w-full shadow-glow">
                  <Video className="h-4 w-4 mr-2" />
                  Create & Start Room
                </Button>
              </CardContent>
            </Card>
          )}

          {meetupRooms.length > 0 ? (
            <div className="space-y-4">
              {meetupRooms.map((room) => (
                <div key={room.meetup_id} className="flex items-center justify-between p-4 rounded-lg glass-hover">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 rounded-full bg-primary/10">
                      <Video className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{room.title}</p>
                      <p className="text-sm text-gray-400">
                        {room.participant_count}/{room.max_participants} participants
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant={room.status === 'active' ? 'default' : 'secondary'}>
                      {room.status}
                    </Badge>
                    {room.status === 'active' && (
                      <Button size="sm" onClick={() => navigate(`/virtual-room/${room.event_id}?meetup=${room.meetup_id}`)}>
                        <Play className="h-4 w-4 mr-2" />
                        Join
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Video className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No meetup rooms yet</p>
              <p className="text-sm text-gray-500">Create a room to connect with your fans!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Your Events */}
      <Card className="glass border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Your Events</CardTitle>
          <Button variant="outline" onClick={() => navigate('/create-event')} className="glass-hover">
            <Plus className="h-4 w-4 mr-2" />
            Create Event
          </Button>
        </CardHeader>
        <CardContent>
          {artistEvents.length > 0 ? (
            <div className="space-y-4">
              {artistEvents.slice(0, 5).map((event) => (
                <div key={event.event_id} className="flex items-center justify-between p-4 rounded-lg glass-hover">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 rounded-full bg-secondary/10">
                      <Calendar className="h-5 w-5 text-secondary" />
                    </div>
                    <div>
                      <p className="font-medium">{event.title}</p>
                      <p className="text-sm text-gray-400">
                        {event.venue} • {new Date(event.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant="outline">{event.tickets_sold || 0} sold</Badge>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/edit-event/${event.event_id}`)}>
                      Manage
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No events yet</p>
              <p className="text-sm text-gray-500">Create your first event to start selling tickets!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
